import { Box, Typography } from "@mui/material";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext } from "react";
import { CustomEvent } from "schemas/functions";

import FollowUpDelayDivider from "components/FollowUpDelayDivider";
import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import FollowupSelect from "components/TemplateScreen/FollowupSelect";
import {
  DAYS_TO_DELAY_FOLLOWUP,
  MAXIMUM_FOLLOWUP_ALLOWED,
} from "constants/templates";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";

import styles from "./styles";

interface Props {
  followups: Template[];
  onChange: (followups: Template[]) => void;
}

export default function Followups({ followups, onChange }: Props) {
  const {
    templates: allTemplates,
    outreachTemplates,
    followupTemplates,
    setCreateTemplateDialogOpen,
    setCurrentTemplateContentType,
  } = useContext(OutreachTemplatesContext);
  const filteredFollowUps = followupTemplates?.filter(
    (t) => !t.emailTemplateId,
  );

  const handleTemplateChange = (e: CustomEvent, index: number) => {
    const template = allTemplates?.find((x) => x.id === e.target.value);
    if (!template) return;
    const copy = [...followups];
    copy[index] = {
      ...template,
      delayDays: copy[index]?.delayDays ?? DAYS_TO_DELAY_FOLLOWUP,
    };
    onChange(copy);
  };

  const handleDelayChange = (index: number, value: number) => {
    const copy = [...followups];
    if (copy[index]) {
      copy[index] = { ...copy[index], delayDays: value };
      onChange(copy);
    }
  };

  const addFollowUp = () =>
    onChange([
      ...followups,
      { ...followupTemplates?.[0], delayDays: DAYS_TO_DELAY_FOLLOWUP },
    ]);

  const removeFollowUp = (index: number) => {
    const copy = [...followups];
    copy.splice(index, 1);
    onChange(copy);
  };

  const createFollowUp = () => {
    setCurrentTemplateContentType(TemplateContentType.FOLLOWUP);
    setCreateTemplateDialogOpen(true);
  };

  return (
    <Box sx={styles.section}>
      <Typography component="label" sx={styles.fieldLabel}>
        Follow-ups (optional){" "}
        <Box component="span" sx={styles.mutedNote}>
          — sent as-is, not AI-personalized
        </Box>
      </Typography>
      {followupTemplates.length === 0 ? (
        <StyledLargeButton
          disableElevation
          disabled={outreachTemplates.length === 0}
          onClick={createFollowUp}
        >
          Create Follow-up
        </StyledLargeButton>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {followups.map((followup, index) => (
            <Box key={index}>
              <FollowUpDelayDivider
                days={followup?.delayDays ?? DAYS_TO_DELAY_FOLLOWUP}
                onChange={(value) => handleDelayChange(index, value)}
                isFirst={index === 0}
                align="start"
              />
              <FollowupSelect
                followup={followup}
                index={index}
                filteredFollowUps={filteredFollowUps}
                handleTemplateChange={handleTemplateChange}
                onRemove={() => removeFollowUp(index)}
              />
            </Box>
          ))}
          {followups.length < MAXIMUM_FOLLOWUP_ALLOWED && (
            <Box sx={{ mt: 2 }}>
              <StyledLargeButton
                variant="outlined"
                disableElevation
                onClick={addFollowUp}
              >
                <Box
                  component="i"
                  className="fa-regular fa-add"
                  sx={{ mr: 0.5 }}
                />{" "}
                Follow Up
              </StyledLargeButton>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
